import React, { useState } from "react";
import Dictionary from "../../utils/Dictionary";
import dictionaryData from "../../../constants/Data";

const Article = () => {
  const [hoveredWord, setHoveredWord] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseOver = (e) => {
    const word = e.target.innerText;
    const definition = dictionaryData[word];

    if (definition) {
      setHoveredWord({ word, definition });
      setPosition({ x: e.clientX, y: e.clientY });
    } else {
      setHoveredWord(null);
    }
  };

  const handleMouseOut = () => {
    setHoveredWord(null);
  };

  return (
    <div>
      {hoveredWord && (
        <Dictionary
          word={hoveredWord.word}
          definition={hoveredWord.definition}
          position={position}
        />
      )}
      <div>
        <h2 id="Problem"> Problem </h2>
        <hr className="dividergeneral mb-5" />

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          <span>Cadmium</span>, a highly toxic heavy metal, poses a growing risk
          to society. It has been identified by the International Agency for
          Cancer as a group-one <span>carcinogen</span> and it is listed as one
          of 126 priority pollutants by the{" "}
          <span>Environmental Protection Agency</span>. The dangerous nature of
          cadmium has led in the past decade to the introduction of measures
          restricting its use in Europe. Notwithstanding, its presence in soil
          continues to increase due to mining and the improper disposal of
          electronic devices, and other objects containing cadmium, affecting
          our food supplies.
        </p>
        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          This soil and water <span>contamination</span> particularly affects
          children and vegetarians
          <a href="#ref1">
            <sup>1</sup>{" "}
          </a>
          , due to <span>bioaccumulation</span> of the heavy metal in{" "}
          <span>crops</span>. As individuals are encouraged to reduce their meat
          consumption and embrace plant-based diets for the environmental
          benefits, they may inadvertently increase their exposure to cadmium.
          The growing trend in vegetarianism indicates that this problem will
          only become more severe with time. Altogether, our team feels this
          issue requires prompt action and a swift resolution.
        </p>
        <h2 id="Current-solutions" className="mt-20">
          Current solutions
        </h2>
        <hr className="dividergeneral mb-5" />

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          Despite the widespread nature of cadmium contamination and human
          exposure, a viable solution to eliminate it remains{" "}
          <span>elusive</span>. <span>Acute</span> cadmium intoxication is
          currently treated firstly by <span>irrigating</span> the
          gastrointestinal tract to remove any cadmium-containing solution. If{" "}
          <span>emesis</span> doesn't occur a gastric lavage is performed
          <a href="#ref2">
            <sup>2</sup>
            {""}
          </a>
          . Another form of treatment is the use of{" "}
          <span>chelating agents</span>, such as EDTA, to increase the
          elimination of cadmium. Unfortunately, some of the chelating agents
          that are normally used as a treatment for heavy metals toxicity are
          not effective for cadmium, that is the case of Penicillamine (DPA),
          and others have dangerous side effects like Dimercaprol
          <a href="#ref3">
            <sup>3</sup>{" "}
          </a>
          For this reason available treatments are not applicable to cases of
          chronic exposure. Therefore, it is crucial to urgently develop a
          solution to prevent the bioaccumulation of cadmium and{" "}
          <span>mitigate</span> its detrimental effects on human health.
        </p>
        <h2 className="mt-20" id="Affected-populations">
          Affected populations
        </h2>
        <hr className="dividergeneral mb-5" />

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          Cadmium contamination affects multiple communities, including those
          close to our EPFL team. A substantial percentage of the European
          Union’s population is exposed to dangerously high levels of cadmium,
          with nearly half of the population in France being affected
          <a href="#ref4">
            <sup>4</sup>
            {""}
          </a>
          . Moreover, the <span>ramifications</span> extend beyond Europe and
          have global implications. Various regions worldwide, including the
          United States, South and East Asia, and South America are{" "}
          <span>grappling</span> with the detrimental effects of cadmium
          contamination. It has been discovered that an alarming 3.0 million
          people in the United States
          <a href="#ref5">
            <sup>5</sup>{" "}
          </a>
          were provided with water that exceeded health guidelines for cadmium
          levels. It is clear to us that this issue is a matter of great
          importance on a global and local scale.
        </p>
        <h2 className="mt-20" id="Our-solution">
          Our solution
        </h2>
        <hr className="dividergeneral mb-5" />

        <h3 id="Solution">
          A Live Biotherapeutic Product (LBP) that effectively targets and
          reduces cadmium absorption in the human gut
        </h3>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          To address the risks associated with cadmium exposure, it is crucial
          to raise public awareness and implement proactive preventive measures.
          Our proposed solution is to develop an implementation of a{" "}
          <span>Live Biotherapeutic Product</span> (LBP), which aims to minimize
          the carcinogenic and toxic accumulation of cadmium in the human body.
        </p>
        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          Our primary objective is to create modified bacteria that can
          temporarily attach to the <span>mucus</span> lining of the human small
          intestine and <span>amass</span> cadmium. While mucus-bound, these
          engineered bacteria will bind with any cadmium that passes through the
          digestive system, effectively preventing its <span>absorption</span>{" "}
          into the body. Subsequently, the bacteria, carrying the cadmium, will
          be naturally eliminated from the body through the normal{" "}
          <span>excretion</span> processes (<a href="#Figure1">Figure 1</a>).
          This approach aims to cleanse the bioaccumulation of cadmium.
        </p>
        <br></br>

        <div class="centered">
          <figure>
            <img
              id="Figure1"
              src="https://static.igem.wiki/teams/4888/wiki/general-description.png"
              alt="project_description"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 1:</u>{" "}
              </b>
              Schematic of our engineered bacteria binding to cadmium within the
              intestines.
            </figcaption>
          </figure>
        </div>
        <br></br>

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          In the initial phase of our project, we propose to employ the
          bacterial strain Escherichia coli, commonly used in the laboratory, as
          a proof-of-concept strain, as it is well suited for laboratory
          manipulations and experiments. However, our ultimate objective is to
          progress towards using bacterial strains that naturally reside in the
          human gut, such as Lactobacillus. By employing these native gut
          bacteria, we can enhance compatibility and minimize potential
          disruptions to the natural <span>microbial ecosystem</span> within the
          intestines.
        </p>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          Our approach is to <span>induce</span> the production of two distinct
          proteins in the <i>E. coli</i> bacteria. The first protein is a
          mucus-binding protein, which will enable the bacteria to temporarily
          attach to the mucus lining of the human intestines. This adhesion
          slows down the passage of the bacteria through the intestine and
          prolongs the duration of action.
        </p>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          Simultaneously, the second protein, a cadmium-binding protein, will
          also be induced. This protein is crucial for the bacteria to
          effectively bind the cadmium present in the digestive system. By
          encoding said protein, we ensure that a significant proportion of the
          cadmium passing through the gut will be captured by the engineered
          bacteria, preventing its absorption into the human body.
        </p>

        <h2 className="mt-20" id="mucus">
          Mucus binding protein{" "}
        </h2>
        <hr className="dividergeneral mb-5" />

        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          The mucus-binding protein we selected for our research, SpaC, is a
          protein that binds effectively to human <span>mucus</span>
          <a href="#ref6">
            <sup>6</sup>
          </a>
          . To ensure the expression of SpaC on the surface of <i>E. coli</i>,
          we have attached it to LPP-OmpA (<a href="#Figure2">Figure 2</a>), an
          outer membrane protein obtained from the 2016 iGEM team of Mingdao
          <a href="#ref7">
            <sup>7</sup>
          </a>
          . This fusion allows for the transport and <span>anchoring</span> of
          our protein to the outer membrane of the <i>E. coli</i>.
        </p>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          To prevent undesired, long retention of the LBP inside the body, we
          have taken several precautions. First, we rely on the natural turnover
          of the mucus layer{" "}
          <a href="#ref8">
            <sup>8</sup>
          </a>
          . As the mucus layer is constantly renewed, the bacteria attached to
          it will naturally be expelled from the body over time. Additionally,
          SpaC will be only expressed in culture. Once the LBP is administered,
          the synthesis stops and over time, the proteins present on the surface
          will <span>degrade</span>. Next, to control induction, we employ a
          lacl operon system through which <span>transcription</span> can be
          induced in presence of <span>IPTG</span>. However, the consumption of
          dairy products can lead to trace amounts of allolactose which is an{" "}
          <span>analogue</span> to IPTG. To avoid undesired expression, we
          introduce a second switch on a translational level. The theophylline{" "}
          <span>riboswitch</span> is a sequence prior a protein sequence that
          causes a hairpin formation of the mRNA
          <a href="#ref9">
            <sup>9</sup>
          </a>
          . The <span>ribosome binding sequence</span> (RBS) is part of the
          folded structure and therefore unavailable to the ribosome machinery.
          The presence of theophylline causes a{" "}
          <span>conformational change</span> of the hairpin structure,
          liberating the RBS and allowing for translation. These measures
          collectively contribute to the efficient departure of <i>E. coli</i>{" "}
          from the digestive tract, facilitating the desired elimination of the
          bacteria after completing its cadmium binding function.
        </p>

        <h2 className="mt-20" id="cadmium-binding">
          Cadmium binding protein{" "}
        </h2>
        <hr className="dividergeneral mb-5" />

        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          The cadmium binding protein of choice is EC20, a protein known for its{" "}
          <span>affinity</span> and specificity towards cadmium
          <a href="#ref10">
            <sup>10</sup>
          </a>
          . EC20 has been previously expressed on the surface of <i>E. coli</i>,
          which allowed the binding of external cadmium8.
        </p>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          Using the same approach than for SpaC, EC20 will be transported and
          anchored to the outer membrane of <i>E. coli</i> by fusion to
          LPP-OmpA. To drive the expression of EC20, we employ a constitutive{" "}
          <span>promoter</span> called BBA_J23100, which originates from the
          2006 Berkeley iGEM team
          <a href="#ref11">
            <sup>11</sup>
          </a>
          . This promoter is known for its reliable and consistent expression
          characteristics. By utilizing the BBA_J23100 promoter, we can ensure
          the continuous production of EC20 in the engineered <i>E. coli</i>{" "}
          strain.
        </p>

        <h2 className="mt-20" id="health">
          Additional health precautions{" "}
        </h2>
        <hr className="dividergeneral mb-5" />

        <h3 id="Zinc"> Zinc & Iron </h3>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          When designing and implementing a LBP (Live Biotherapeutic product)
          for human consumption, several health precautions must be considered.
          For instance, while EC20 is highly specific for cadmium, it has low
          affinity for other metals, which increases the risk of depleting
          essential minerals like zinc or iron from the gut in the absence of
          cadmium. To address this, we will administer our LBP with zinc and
          iron
          <a href="#ref12">
            <sup>12</sup>
          </a>
          , optimizing protein stability and preventing the{" "}
          <span>depletion</span> of vital ions. We will ensure that the{" "}
          <span>concurrent</span> administration does not exceed the recommended
          daily doses for these metals, thereby maintaining essential nutrient
          balance in the body while effectively addressing cadmium
          bioaccumulation concerns.
        </p>
        <br></br>
        <h3 id="HGT"> Horizontal Gene Transfer (HGT) Safeguard </h3>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          Additionally, we must guard against{" "}
          <span>Horizontal Gene Transfer</span> (HGT) . Once our LBP is
          administered, it will interact with other gut bacteria, posing the
          risk of HGT. This is concerning because introducing artificial DNA
          sequences into natural bacteria can have unpredictable consequences,
          including the exchange of <span>antibiotic resistance</span> genes. To{" "}
          <span>mitigate</span> this risk, we employ a dut/ung double gene
          system knockout strategy. This system progressively incorporates
          uracil into the genome of the donor strain, leading to double-stranded
          DNA breaks and eventual degradation of the transferred DNA strand
          <a href="#ref13">
            <sup>13</sup>
          </a>
          .
        </p>
        <h2 className="mt-20" id="Engagement">
          Engagement with the community{" "}
        </h2>
        <hr className="dividergeneral mb-5" />

        <h3 id="Education"> Education </h3>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          Throughout the course of our project, we centered our educational
          effort around creating diverse and tailored initiatives to cater to
          our target audiences.
        </p>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          As such, we develop a multitude of educational proposals, mainly
          focused on educating younger students. Some were designed for direct
          engagement with students, while others aimed to reach those we might
          not encounter in person.
        </p>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          Regardless of their diverse forms, all these initiatives shared a
          common focus: educating the community about synthetic biology and our
          project's objective of preventing cadmium intoxication. For more
          information about our education campaign, please visit our{" "}
          <a href="../education"> Education page</a>.
        </p>
        <br></br>

        <h3 id="Hp"> Human Practices </h3>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          For our Human Practices efforts, our team engaged in dialogues with
          experts from diverse backgrounds. Initially, we focused on the
          academic sphere to assess the problem and gain valuable insights into
          the most effective approaches to apply in our wet lab work.
          Subsequently, we engaged in discussions with professionals from the
          industry, as well as experts in the fields of medicine and
          pharmaceuticals, in order to precisely delineate the criteria our
          product needed to meet. Finally, through conversations with a LBP
          startup and the help of surveys, we were able to refine our
          understanding of how to successfully introduce our product to the
          market. For more information about our Human Practices work, please
          visit our <a href="../human-practices"> Human Practicies page</a>.
        </p>
        <br></br>

        <h3 id="Sustainability"> Sustainability </h3>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          In line with our commitment to sustainability, we maintained an
          ongoing dialogue with Michka Mélo, a sustainability coach at EPFL. We
          conducted multiple meetings during which we shared our progress and
          uncertainties with him, and he provided valuable guidance, fostering
          insightful reflections. Michka assisted us in considering how to
          present the issue of cadmium pollution, our solution, and, notably,
          helped us devise a business model that ensures the sustainable
          utilization of our LBP, in conjunction with other sustainability
          measures. For more information about our work on sustainability,
          please visit our{" "}
          <a href="../human-practices#sustainability">
            {" "}
            Sustainibility section
          </a>
          .
        </p>
        <br></br>

        <h2 id="Future" className="mt-20">
          Future perspectives
        </h2>
        <hr className="dividergeneral mb-5" />

        <p className="mb-2">
          We have successfully shown that our LBP can capture cadmium, attach to
          mucus, and that the anchor protein degrades through natural processes.
          However, as we continue our journey towards the final product, there
          are still unresolved questions that require further examination and
          evaluation.<br></br>
          Certain experiments need to be conducted before assembling the
          components. First, the half-life we obtained for D1D2 was assessed in
          gentle conditions. In contrast, the intestinal environment is
          considerably harsher and can potentially reduce the stability and,
          consequently, the half-life of surface proteins. It is crucial to
          repeat the experiment under conditions that better mimic the gut
          environment.<br></br>
          The Mucus-binding experiment should also be repeated with an added
          time dimension. This would involve plotting the binding capacity
          against the surface levels of the D1D2 construct, which decreases over
          time, to assess how the binding capacity diminishes over the course of
          the experiment.<br></br>
          Additionally, it's worth considering exploring various induction
          conditions to adjust the initial protein levels. This can potentially
          affect the time it takes for the mucus binding capacity to decrease to
          a critical point, leading to the release of the bacterium.<br></br>
          Lao et al. (2007) demonstrated the high selectivity of EC20 for
          cadmium
          <a href="#ref14">
            <sup>14</sup>
          </a>
          . Nonetheless, it is essential to perform experiments that confirm our
          LBP does not deplete essential metals from the patient's digestive
          tract. This experiment could be done by co-incubation of EC20
          expressing bacteria with cadmium, zinc, and Iron ions. The depletion
          for all these metals will be measured subsequently.<br></br>
          Once selectivity is confirmed, we still need to address the issue of
          cross affinity of EC20 to zinc and Iron. In absence of cadmium, it
          will despite its selectivity for Cadmium, still bind to zinc. To
          address this problem, we will load the EC20 proteins with zinc as it
          was done by Xu et al.
          <a href="#ref15">
            <sup>15</sup>
          </a>
          . Different potential ways of loading would be possible and would need
          to be assessed. <br></br>
          Bacteria could be incubated in a zinc solution, allowing all surface
          protein to bind an ion. Subsequently, a washing step would remove
          excess non-bound metal ions. In this way, with all binding pockets
          initially occupied, for each cadmium ion removed from the intestine,
          an initially bound zinc ion would be released. This approach
          establishes a zero-sum game scenario.<br></br>
          With this zinc loading, washing method, the product would zero-sum
          game for zinc in absence of cadmium. However, zinc load could also be
          monitored to combine both zinc supplementation, which has beneficial
          health effects, and cadmium uptake
          <a href="#ref16">
            <sup>16</sup>
          </a>{" "}
          in only one LBP.<br></br>
          Once all these questions are addressed, we can move to the final step:
          the assembly of the complete biological machine. All the tests done so
          far assessed functionality of individual parts. To finalise the
          project, the parts would need to be assembled and the tests repeated
          to check for possible conflicting interactions between the components.
          <br></br>
          Assembling every part in one system would entail incorporation of the
          EC20 construct, the D1D2 construct, and the DUT and UNG genes
          mutations into the chassis genome, preventing the issue of plasmid
          loss and reducing the risk of DNA transfer to other bacteria. However,
          the E. Coli lab strains like BL21 that we used are not suited for the
          LBP application. A fitter chassis for a live biotherapeutic product
          would be the E. Coli Nissle strain, that was already used for similar
          applications
          <a href="#ref17">
            <sup>17</sup>
          </a>
          . Further, to prevent the spread of any genetically modified organisms
          into the environment or the patient’s intestines, the strain would be
          designed auxotroph as a containment strategy. Possible options would
          be auxotrophy for Diaminopimelic Acid (dap) as demonstrated by
          Puurunen et al. (2021)
          <a href="#ref17">
            <sup>17</sup>
          </a>
          or synthetic auxotrophies explored by Lopez et al. (2015)
          <a href="#ref18">
            <sup>18</sup>
          </a>
          .<br></br>The final engineered live biotherapeutic product would then
          be administered in form of a lyophilized product. For more
          information, please visit our{" "}
          <a href="../product-page"> Product page</a>.
        </p>

        <h2 id="references" className="mt-20">
          References{" "}
        </h2>
        <hr className="dividergeneral mb-5" />

        <ul>
          <ol type="1">
            <li id="ref1">
              {" "}
              HBM4EU (2022) <br />{" "}
              <a href="https://www.hbm4eu.eu/wp-content/uploads/2022/07/HBM4EU_Policy-Brief-Cadmium.pdf">
                {" "}
                Policy brief{" "}
              </a>{" "}
            </li>

            <li id="ref2">
              {" "}
              Rafati Rahimzadeh M, Rafati Rahimzadeh M, Kazemi S, Moghadamnia
              AA. <br />{" "}
              <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5596182/">
                Cadmium toxicity and treatment: An update. Caspian J Intern Med.
                2017 Summer
              </a>{" "}
              <br />
              ChemBioChem 21.22 (2020): 3255-3265.{" "}
            </li>
            <li id="ref3">
              {" "}
              Gonick HC <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/19106433/">
                Nephrotoxicity of cadmium & lead. Indian J Med Res. 2008
              </a>{" "}
              <br />
              ChemBioChem 21.22 (2020): 3255-3265.{" "}
            </li>
            <li id="ref4">
              {" "}
              Santé publique France (2021) <br />{" "}
              <a href="https://www.santepubliquefrance.fr/docs/impregnation-de-la-population-francaise-par-le-cadmium.-programme-national-de-biosurveillance-esteban-2014-2016#:~:text=Chez%20les%20enfants%20la%20consommation,de%20coquillages%20et%20de%20crustac%C3%A9s">
                {" "}
                Imprégnation de la population française par le cadmium.
                Programme National de Biosurveillance{" "}
              </a>
            </li>
            <li id="ref5">
              {" "}
              Jones, Yu ,Guo, Duan and Jia (2022) <br />{" "}
              <a href="https://www.mdpi.com/1660-4601/19/3/1105">
                {" "}
                Racial disparities in the heavy metal contamination of urban
                soil in the Southeastern United States.{" "}
              </a>{" "}
              <br /> International Journal of Environmental Research and Public
              Health, 19(3), 1105.{" "}
            </li>
            <li id="ref6">
              {" "}
              Kankainen, Paulin, Tynkkynen, von Ossowski, Reunanen, Partanen and
              de Vos (2009) <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/19805152/">
                {" "}
                Comparative genomic analysis of Lactobacillus rhamnosus GG
                reveals pili containing a human-mucus binding protein.
              </a>{" "}
              <br /> Proceedings of the National Academy of Sciences, 106(40),
              17193-17198.{" "}
            </li>
            <li id="ref7">
              {" "}
              <a href="https://2016.igem.org/Team:Mingdao">
                {" "}
                iGEM: Team Mingdao (2016){" "}
              </a>{" "}
            </li>
            <li id="ref8">
              {" "}
              Schneider, Pelaseyed, Svensson and Johansson (2018)
              <br />{" "}
              <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5893601/">
                {" "}
                Study of mucin turnover in the small intestine by in vivo
                labeling.{" "}
              </a>{" "}
              <br /> Scientific reports, 8(1), 1-11.{" "}
            </li>

            <li id="ref9">
              {" "}
              Topp, Reynoso, Seeliger, Goldlust, Desai, Murat and Gallivan
              (2010) <br />{" "}
              <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2988590/">
                Synthetic riboswitches that induce gene expression in diverse
                bacterial species.{" "}
              </a>{" "}
              <br /> Applied and environmental microbiology, 76(23), 7881-7884.{" "}
            </li>
            <li id="ref10">
              {" "}
              Bae, Chen, Mulchandani and Mehra (2000) <br />{" "}
              <a href="https://onlinelibrary.wiley.com/doi/abs/10.1002/1097-0290%2820001205%2970%3A5%3C518%3A%3AAID-BIT6%3E3.0.CO%3B2-5">
                Enhanced bioaccumulation of heavy metals by bacterial cells
                displaying synthetic phytochelatins.
              </a>{" "}
              <br /> Biotechnology and bioengineering, 70(5), 518-524.{" "}
            </li>
            <li id="ref11">
              {" "}
              <a href="https://2006.igem.org/wiki/index.php/Berkeley">
                {" "}
                iGEM: Team Berkeley (2006)
              </a>
            </li>
            <li id="ref12">
              {" "}
              Xu, Bae, Mulchandani, Mehra and Chen <br />{" "}
              <a href="https://pubs.acs.org/doi/10.1021/bm015631f">
                Heavy metal removal by novel CBD-EC20 sorbents immobilized on
                cellulose.
              </a>{" "}
              <br /> Biomacromolecules, 3(3), 462-465.
            </li>

            <li id="ref13">
              {" "}
              Algar, Al-Ramahi, de Lorenzo and Martinez-Garcia <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/32597553/">
                Environmental Performance of Pseudomonas putida with a
                Uracylated Genome
              </a>{" "}
              <br />
              ChemBioChem 21.22 (2020): 3255-3265.{" "}
            </li>
            <li id="ref14">
              {" "}
              Lao, U. Loi, et al. <br />{" "}
              <a href="https://onlinelibrary.wiley.com/doi/abs/10.1002/bit.21478">
                Cadmium removal from contaminated soil by thermally responsive
                elastin (ELPEC20) biopolymers
              </a>{" "}
              <br />
              Biotechnology and bioengineering 98.2 (2007): 349-355.{" "}
            </li>
            <li id="ref15">
              {" "}
              Xu, Zhaohui, et al. <br />{" "}
              <a href="https://pubs.acs.org/doi/full/10.1021/bm015631f">
                Heavy metal removal by novel CBD-EC20 sorbents immobilized on
                cellulose
              </a>{" "}
              <br />
              Biomacromolecules 3.3 (2002): 462-465.{" "}
            </li>
            <li id="ref16">
              {" "}
              Dr. Enrico Fiaccadori <br></br>
              Interview{" "}
            </li>
            <li id="ref17">
              {" "}
              Puurunen, Marja K., et al. <br />{" "}
              <a href="https://www.nature.com/articles/s42255-021-00430-7">
                Safety and pharmacodynamics of an engineered E. coli Nissle for
                the treatment of phenylketonuria: a first-in-human phase 1/2a
                study
              </a>{" "}
              <br />
              Nature Metabolism 3.8 (2021): 1125-1132.{" "}
            </li>
            <li id="ref18">
              {" "}
              Lopez, Gabriel, and J. Christopher Anderson. <br />{" "}
              <a href="https://pubs.acs.org/doi/full/10.1021/acssynbio.5b00085">
                Synthetic auxotrophs with ligand-dependent essential genes for a
                BL21 (DE3) biosafety strain
              </a>{" "}
              <br />
              ACS Synthetic Biology 4.12 (2015): 1279-1286.{" "}
            </li>
          </ol>
        </ul>
        <br></br>
      </div>
    </div>
  );
};

export default Article;
